import React, { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import PermissionElementWrapper from '../../../../../pages/PermissionControls/PermissionElementWrapper';
import { IMeasureFieldsFragment } from '../../../../../utils/apollo/graphqlSchema';
import * as FormComponents from '../../../../Form';
import {
  assertNotNil,
  ICreatableReactSelectOption,
} from '../../../../../utils/types';
import BuilderMeasureMenu from '../../../../Builder/Measures/BuilderMeasureMenu';
import { ISaveMeasureState } from './types';

interface IProps {
  defaultNamedMeasure: IMeasureFieldsFragment | null;
  isPermittedToReadMeasure: boolean;
  categories: ICreatableReactSelectOption[];
  setCategories: (categories: ICreatableReactSelectOption[]) => void;
  setIsEntityManaged: (isEntityManaged: boolean) => void;
}

const NamedMeasureField = ({
  defaultNamedMeasure,
  isPermittedToReadMeasure,
  categories,
  setCategories,
  setIsEntityManaged,
}: IProps) => {
  const [selectedNamedMeasure, setSelectedNamedMeasure] =
    useState<IMeasureFieldsFragment | null>(defaultNamedMeasure);

  const { control, setValue } = useFormContext<ISaveMeasureState>();

  const {
    field,
    fieldState,
    formState: { isSubmitting },
  } = useController({
    name: 'versionId',
    control,
  });

  const handleSelectNamedMeasure = (measure: IMeasureFieldsFragment) => {
    assertNotNil(measure?.version);

    setSelectedNamedMeasure(measure);

    const { description, versionId } = measure.version;

    field.onChange(versionId);

    if (description) {
      setValue('description', description);
    }

    setCategories(
      measure?.categories?.map((cat) => ({
        label: cat.name,
        value: cat.id,
      })) ?? [],
    );

    if (measure.isManaged) {
      setIsEntityManaged(true);
    }
  };

  const handleClear = () => {
    field.onChange(null);
    setSelectedNamedMeasure(null);
    if (categories.length > 0) {
      setCategories([]);
    }
  };

  const error = fieldState.error?.message;

  return (
    <FormComponents.VerticalField>
      <FormComponents.FieldLabel>Name</FormComponents.FieldLabel>
      <PermissionElementWrapper isPermitted={isPermittedToReadMeasure}>
        <BuilderMeasureMenu
          defaultNamedMeasure={selectedNamedMeasure}
          onChange={handleSelectNamedMeasure}
          onClear={handleClear}
          allowRollingControl={true}
          allowCompoundMeasure={true}
          isError={Boolean(error)}
          isDisabled={!isPermittedToReadMeasure || isSubmitting}
        />
      </PermissionElementWrapper>
      {error && <FormComponents.FieldError type="custom" customError={error} />}
    </FormComponents.VerticalField>
  );
};

export default NamedMeasureField;
